import { Check, Copy, UserPlus, X } from 'lucide-react'
import type React from 'react'
import { useRef, useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { Modal } from '../common/Modal'

const ModalTitle = styled.h2`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  transition: ${theme.transitions.default};
  margin-left: auto;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background-color: ${({ theme }) => theme.colors.background};
  }
`

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.lg};
`

const ModalBodyContent = styled.div`
  padding: ${theme.spacing.lg};
`

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.md};
  line-height: 1.5;
  margin-bottom: ${theme.spacing.lg};
`

const UrlContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
`

const UrlInput = styled.input`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
  font-family: 'Monaco', 'Menlo', monospace;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:read-only {
    cursor: pointer;
  }
`

const CopyButton = styled.button<{ $copied: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.md};
  background-color: transparent;
  color: ${(props) => (props.$copied ? '#000000' : props.theme.colors.text)};
  border: 0;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: ${theme.transitions.default};
  font-size: ${theme.typography.sm};
  font-weight: 500;
  min-width: 24px;
  justify-content: center;

  &:hover {
    background-color: ${(props) => (props.$copied ? props.theme.colors.primaryDark : props.theme.colors.surfaceHover)};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`

const InstructionsList = styled.ul`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
  line-height: 1.5;
  margin: 0;
  padding-left: ${theme.spacing.lg};

  li {
    margin-bottom: ${theme.spacing.xs};
  }
`

const ModalFooterContent = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.lg};
`

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.md};
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};
  border: 1px solid transparent;

  ${(props) =>
    props.$variant === 'primary'
      ? `
    background-color: ${props.theme.colors.primary};
    color: #000000;

    &:hover {
      background-color: ${props.theme.colors.primaryDark};
    }
  `
      : `
    background-color: transparent;
    color: ${props.theme.colors.textMuted};
    border-color: ${props.theme.colors.border};

    &:hover {
      background-color: ${props.theme.colors.background};
      color: ${props.theme.colors.text};
    }
  `}

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}40;
  }
`

interface InviteAthleteModalProps {
  isOpen: boolean
  onClose: () => void
  coachId: string
  coachName: string
}

export const InviteAthleteModal: React.FC<InviteAthleteModalProps> = ({ isOpen, onClose, coachId, coachName }) => {
  const [copied, setCopied] = useState(false)
  const urlInputRef = useRef<HTMLInputElement>(null)

  // Generate the invite URL with coach ID
  const baseUrl = window.location.origin
  const inviteUrl = `${baseUrl}/signup/athlete?coach=${encodeURIComponent(coachId)}`

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  const handleUrlClick = () => {
    // Select the URL text when clicked
    if (urlInputRef.current) {
      urlInputRef.current.select()
    }
  }

  const headerContent = (
    <HeaderContent>
      <ModalTitle>
        <UserPlus size={20} />
        Invite Athlete
      </ModalTitle>
      <CloseButton onClick={onClose}>
        <X size={20} />
      </CloseButton>
    </HeaderContent>
  )

  const footerContent = (
    <ModalFooterContent>
      <Button $variant="secondary" onClick={onClose}>
        Close
      </Button>
    </ModalFooterContent>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="800px" header={headerContent} footer={footerContent}>
      <ModalBodyContent>
        <Description>
          Share this link with your athletes to allow them to connect them to your coaching account.
        </Description>

        <UrlContainer>
          <UrlInput ref={urlInputRef} type="text" value={inviteUrl} readOnly onClick={handleUrlClick} />
          <CopyButton $copied={copied} onClick={handleCopyToClipboard}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </CopyButton>
        </UrlContainer>

        <InstructionsList>
          <li>Athletes click the link and see your name ({coachName}) during signup</li>
          <li>They only need to provide their email address (no password required)</li>
          <li>Once they complete signup, they'll be automatically connected to your coaching account</li>
          <li>You'll see them appear in your athletes list immediately</li>
          <li>Once an athlete is connected, you can include him/her in your automated analysis</li>
        </InstructionsList>
      </ModalBodyContent>
    </Modal>
  )
}
